import React, { useEffect, useState } from 'react';
import { Box, Typography, FormControl, InputLabel, Select, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Dialog, DialogContent, DialogTitle, DialogActions, Button, Alert } from '@mui/material';
import SalarySlipTemplate from './SalarySlipTemplate';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

const API_URL = process.env.REACT_APP_API_URL;
const EMP_API = `${API_URL}/api/employees`;
const months = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];
const currentYear = new Date().getFullYear();
const years = Array.from({length: 7}, (_, i) => (currentYear - 3 + i).toString());

export default function SalarySlipView({ user }) {
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const [slips, setSlips] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [error, setError] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    fetch(EMP_API).then(r => r.json()).then(setEmployees);
  }, []);

  useEffect(() => {
    if (selected && month && year) {
      fetch(`${API_URL}/api/salary-slip/${selected}/${month}/${year}`)
        .then(r => r.json())
        .then(data => {
          if (Array.isArray(data) && data.length === 1) {
            const slip = data[0];
            const emp = employees.find(e => e.employee_id === selected);
            setSlips([
              {
                ...slip,
                actualSalary: slip.actualSalary || slip.actual_salary,
                specialAllowance: slip.specialAllowance || slip.special_allowance,
                foodAllowance: slip.foodAllowance || slip.food_allowance,
                engagementFund: slip.engagementFund || slip.engagement_fund,
                telephone: slip.telephone,
                totalAllowance: slip.totalAllowance || slip.total_allowances,
                gross: slip.gross || slip.gross_salary,
                net: slip.net || slip.net_salary,
                totalDays: slip.totalDays || slip.total_days,
                leaveBalance: emp ? emp.leave_balance : slip.leaveBalance
              }
            ]);
          } else {
            setSlips([]);
          }
        });
    } else {
      setSlips([]);
    }
  }, [selected, month, year, employees]);

  useEffect(() => {
    if (user && user.role === 'employee') {
      setSelected(user.employee_id);
    }
  }, [user]);

  useEffect(() => {
    const now = new Date();
    const selectedDate = new Date(year, months.indexOf(month), 1);
    if (selectedDate > now) {
      setError('Please select a valid month and year.');
    } else {
      setError('');
    }
  }, [month, year]);

  return (
    <Box>
      {user && user.role === 'employee' ? (
        <Typography variant="h6" sx={{ mb: 2 }}>Salary Slips for {user.name}</Typography>
      ) : (
        <FormControl sx={{ minWidth: 200, mb: 2 }}>
          <InputLabel>Employee</InputLabel>
          <Select
            value={selected}
            onChange={e => setSelected(e.target.value)}
            label="Employee"
          >
            <MenuItem value=""><em>None</em></MenuItem>
            {employees.map(emp => (
              <MenuItem key={emp.employee_id} value={emp.employee_id}>{emp.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Month</InputLabel>
          <Select value={month} label="Month" onChange={e => setMonth(e.target.value)}>
            <MenuItem value=""><em>None</em></MenuItem>
            {months.map(m => (
              <MenuItem key={m} value={m}>{m}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 100 }}>
          <InputLabel>Year</InputLabel>
          <Select value={year} label="Year" onChange={e => setYear(e.target.value)}>
            <MenuItem value=""><em>None</em></MenuItem>
            {years.map(y => (
              <MenuItem key={y} value={y}>{y}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {slips.length === 1 && (
          <>
            <Button variant="outlined" onClick={() => setPreviewOpen(true)} disabled={!!error}>Preview</Button>
            <Button variant="outlined" color="primary" onClick={() => document.querySelector('.salary-slip-actions button')?.click()} disabled={!!error}>Download PDF</Button>
          </>
        )}
      </Box>
      {!error && slips.length === 1 && slips[0].workingDays !== undefined ? (
        <Box
          sx={{
            width: '100%',
            overflowX: isMobile ? 'auto' : 'visible',
            borderRadius: 2,
            border: '1px solid #e0e0e0',
            background: '#fff',
            boxShadow: 1,
            mb: 2,
            px: isMobile ? 1 : 3,
            py: 2,
          }}
        >
          <div style={{ minWidth: isMobile ? 400 : '100%', fontSize: isMobile ? 12 : 14 }}>
            <SalarySlipTemplate slip={slips[0]} />
          </div>
        </Box>
      ) : !error && (
        <Alert severity="error" sx={{ mb: 2 }}>No data available for the selected month and year.</Alert>
      )}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Salary Slip Preview</DialogTitle>
        <DialogContent>
          <SalarySlipTemplate slip={slips[0]} hideActions={true} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 